import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import {
  Edit,
  Visibility,
  CloudUpload,
  Delete,
  Add,
  History,
  Image as ImageIcon,
  Article,
  Save,
  Preview,
  Gavel,
  Notifications
} from '@mui/icons-material';
import axios from 'axios';

// Import ReactQuill - using react-quill-new for React 19 compatibility
// We'll use a try-catch wrapper component to handle missing package
let ReactQuillComponent: any = null;

// Try to load ReactQuill
try {
  // @ts-ignore - react-quill-new may not be installed
  const ReactQuillModule = require('react-quill-new');
  ReactQuillComponent = ReactQuillModule.default || ReactQuillModule;
} catch (e) {
  // Package not installed - will use fallback TextField
  console.warn('react-quill-new not found. Using fallback editor.');
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`content-tabpanel-${index}`}
      aria-labelledby={`content-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface ContentPage {
  id: string;
  title: string;
  titleFr?: string;
  slug: string;
  content: string;
  contentFr?: string;
  status: 'draft' | 'published';
  seoTitle?: string;
  seoDescription?: string;
  lastModified: string;
  version: number;
  publishedAt?: string | null;
}

interface MarketingAsset {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
  uploadedAt: string;
  category: 'banner' | 'logo' | 'promotional';
}

interface LegalDocument {
  terms: ContentPage | null;
  privacy: ContentPage | null;
}

const ContentPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [pages, setPages] = useState<ContentPage[]>([]);
  const [assets, setAssets] = useState<MarketingAsset[]>([]);
  const [legalDocs, setLegalDocs] = useState<LegalDocument>({ terms: null, privacy: null });
  const [loading, setLoading] = useState(false);

  // Page Editor State
  const [editorOpen, setEditorOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<ContentPage | null>(null);
  const [pageTitle, setPageTitle] = useState('');
  const [pageTitleFr, setPageTitleFr] = useState('');
  const [pageSlug, setPageSlug] = useState('');
  const [pageContent, setPageContent] = useState('');
  const [pageContentFr, setPageContentFr] = useState('');
  const [pageStatus, setPageStatus] = useState<'draft' | 'published'>('draft');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  // Legal Document Editor State
  const [legalEditorOpen, setLegalEditorOpen] = useState(false);
  const [currentLegalType, setCurrentLegalType] = useState<'terms' | 'privacy' | null>(null);
  const [legalContent, setLegalContent] = useState('');
  const [legalContentFr, setLegalContentFr] = useState('');
  const [legalTitle, setLegalTitle] = useState('');
  const [legalTitleFr, setLegalTitleFr] = useState('');
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [legalVersions, setLegalVersions] = useState<any[]>([]);
  const [versionsOpen, setVersionsOpen] = useState(false);

  // Asset Upload State
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assetCategory, setAssetCategory] = useState<'banner' | 'logo' | 'promotional'>('banner');

  const [alert, setAlert] = useState<{ show: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    show: false,
    message: '',
    severity: 'success'
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchContent();
  }, [tabValue]);

  // Load Quill CSS when legal editor opens
  useEffect(() => {
    if (legalEditorOpen) {
      // Dynamically load Quill CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';
      link.id = 'quill-stylesheet';

      // Only add if not already present
      if (!document.getElementById('quill-stylesheet')) {
        document.head.appendChild(link);
      }

      return () => {
        // Optional: remove stylesheet when dialog closes (or keep it for better UX)
        // const stylesheet = document.getElementById('quill-stylesheet');
        // if (stylesheet) stylesheet.remove();
      };
    }
  }, [legalEditorOpen]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      if (tabValue === 0) {
        const response = await axios.get(`${apiUrl}/admin/content/pages`, { headers });
        setPages(response.data.pages || []);
      } else if (tabValue === 1) {
        const response = await axios.get(`${apiUrl}/admin/content/assets`, { headers });
        setAssets(response.data.assets || []);
      } else if (tabValue === 2) {
        const response = await axios.get(`${apiUrl}/admin/content/legal`, { headers });
        console.log('Legal documents response:', response.data);
        setLegalDocs({
          terms: response.data.terms || null,
          privacy: response.data.privacy || null
        });
      }
    } catch (error: any) {
      console.error('Failed to fetch content:', error);
      showAlert('error', error.response?.data?.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLegalEditor = (type: 'terms' | 'privacy', startInFrench: boolean = false) => {
    setCurrentLegalType(type);
    if (startInFrench) {
      setEditorLangTab(1);
    } else {
      setEditorLangTab(0);
    }
    const doc = type === 'terms' ? legalDocs.terms : legalDocs.privacy;
    if (doc) {
      setLegalTitle(doc.title);
      setLegalTitleFr(doc.titleFr || '');
      setLegalContent(doc.content);
      setLegalContentFr(doc.contentFr || '');
    } else {
      setLegalTitle(type === 'terms' ? 'Terms of Service' : 'Privacy Policy');
      setLegalTitleFr(type === 'terms' ? 'Conditions d\'utilisation' : 'Politique de confidentialité');
      setLegalContent('');
      setLegalContentFr('');
    }
    setNotifyUsers(false);
    setLegalEditorOpen(true);
  };

  const [editorLangTab, setEditorLangTab] = useState(0);

  const handleCloseLegalEditor = () => {
    setLegalEditorOpen(false);
    setCurrentLegalType(null);
    setLegalContent('');
    setLegalContentFr('');
    setLegalTitle('');
    setLegalTitleFr('');
    setNotifyUsers(false);
    setEditorLangTab(0);
  };

  const handleSaveLegalDocument = async (publish: boolean = false) => {
    if (!currentLegalType) {
      showAlert('error', 'No document type selected');
      return;
    }

    if (!legalContent || !legalContent.trim()) {
      showAlert('error', 'Content is required');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      console.log(`Saving ${currentLegalType} document:`, { title: legalTitle, contentLength: legalContent.length });

      const response = await axios.put(
        `${apiUrl}/admin/content/legal/${currentLegalType}`,
        {
          title: legalTitle || (currentLegalType === 'terms' ? 'Terms of Service' : 'Privacy Policy'),
          titleFr: legalTitleFr || (currentLegalType === 'terms' ? 'Conditions d\'utilisation' : 'Politique de confidentialité'),
          content: legalContent,
          contentFr: legalContentFr,
          notifyUsers: publish && notifyUsers
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log(`${currentLegalType} document saved:`, response.data);

      showAlert('success', publish
        ? `Document published${notifyUsers ? ' and notifications sent' : ''} successfully`
        : 'Document saved successfully'
      );

      handleCloseLegalEditor();
      fetchContent();
    } catch (error: any) {
      console.error(`Error saving ${currentLegalType} document:`, error);
      showAlert('error', error.response?.data?.message || `Failed to save ${currentLegalType === 'terms' ? 'Terms of Service' : 'Privacy Policy'}`);
    }
  };

  const handleViewVersions = async (type: 'terms' | 'privacy') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${apiUrl}/admin/content/legal/${type}/versions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLegalVersions(response.data.versions || []);
      setCurrentLegalType(type);
      setVersionsOpen(true);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to load versions');
    }
  };

  const handleOpenEditor = (page?: ContentPage) => {
    if (page) {
      setCurrentPage(page);
      setPageTitle(page.title);
      setPageTitleFr(page.titleFr || '');
      setPageSlug(page.slug);
      setPageContent(page.content);
      setPageContentFr(page.contentFr || '');
      setPageStatus(page.status);
      setSeoTitle(page.seoTitle || '');
      setSeoDescription(page.seoDescription || '');
    } else {
      setCurrentPage(null);
      setPageTitle('');
      setPageTitleFr('');
      setPageSlug('');
      setPageContent('');
      setPageContentFr('');
      setPageStatus('draft');
      setSeoTitle('');
      setSeoDescription('');
    }
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setCurrentPage(null);
  };

  const handleSavePage = async (publish: boolean = false) => {
    try {
      const token = localStorage.getItem('adminToken');
      const pageData = {
        title: pageTitle,
        titleFr: pageTitleFr,
        slug: pageSlug,
        content: pageContent,
        contentFr: pageContentFr,
        status: publish ? 'published' : pageStatus,
        seoTitle: seoTitle,
        seoDescription: seoDescription
      };

      if (currentPage) {
        await axios.put(
          `${apiUrl}/admin/content/pages/${currentPage.id}`,
          pageData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showAlert('success', 'Page updated successfully');
      } else {
        await axios.post(
          `${apiUrl}/admin/content/pages`,
          pageData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showAlert('success', 'Page created successfully');
      }

      handleCloseEditor();
      fetchContent();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to save page');
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${apiUrl}/admin/content/pages/${pageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Page deleted successfully');
      fetchContent();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to delete page');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUploadAsset = async () => {
    if (!selectedFile) {
      showAlert('error', 'Please select a file to upload');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', assetCategory);

      await axios.post(
        `${apiUrl}/admin/content/assets/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      showAlert('success', 'Asset uploaded successfully');
      setUploadOpen(false);
      setSelectedFile(null);
      fetchContent();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to upload asset');
    }
  };

  const handleDeleteAsset = async (assetId: string) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${apiUrl}/admin/content/assets/${assetId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Asset deleted successfully');
      fetchContent();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to delete asset');
    }
  };

  const showAlert = (severity: 'success' | 'error' | 'info', message: string) => {
    setAlert({ show: true, message, severity });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '3rem' }}>
          Content Management
        </Typography>
        {tabValue === 0 && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenEditor()}
          >
            New Page
          </Button>
        )}
        {tabValue === 1 && (
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadOpen(true)}
          >
            Upload Asset
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<Article />} label="Static Pages" iconPosition="start" sx={{ fontSize: '1.125rem' }} />
          <Tab icon={<ImageIcon />} label="Marketing Assets" iconPosition="start" sx={{ fontSize: '1.125rem' }} />
          <Tab icon={<Gavel />} label="Legal Documents" iconPosition="start" sx={{ fontSize: '1.125rem' }} />
        </Tabs>
      </Paper>

      {/* Static Pages Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {pages.map((page) => (
            <Card key={page.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
                    {page.title}
                  </Typography>
                  <Chip
                    label={page.status}
                    color={page.status === 'published' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '1.125rem' }}>
                  Slug: /{page.slug}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.9375rem' }}>
                  Version {page.version} • Last modified: {new Date(page.lastModified).toLocaleDateString()}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => handleOpenEditor(page)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => {
                    setCurrentPage(page);
                    setPreviewOpen(true);
                  }}
                >
                  Preview
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeletePage(page.id)}
                >
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>

        {pages.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Article sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography color="textSecondary" sx={{ fontSize: '1.125rem' }}>
              No pages yet. Click "New Page" to create your first content page.
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Legal Documents Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Terms of Service Card */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
                  Terms of Service
                </Typography>
                <Chip
                  label={legalDocs.terms?.status || 'Not Created'}
                  color={legalDocs.terms?.status === 'published' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              {legalDocs.terms ? (
                <>
                  <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '1.125rem' }}>
                    Version {legalDocs.terms.version} • Last updated: {new Date(legalDocs.terms.lastModified).toLocaleDateString()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.9375rem' }}>
                      Published: {legalDocs.terms.publishedAt ? new Date(legalDocs.terms.publishedAt).toLocaleDateString() : 'Not published'}
                    </Typography>
                    <Chip
                      label={legalDocs.terms.contentFr ? 'FR Version: OK' : 'FR Version: Missing'}
                      size="small"
                      variant="outlined"
                      color={legalDocs.terms.contentFr ? 'success' : 'warning'}
                    />
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '1.125rem' }}>
                  No Terms of Service document created yet.
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => handleOpenLegalEditor('terms', false)}
              >
                {legalDocs.terms ? 'Edit (EN)' : 'Create'}
              </Button>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => handleOpenLegalEditor('terms', true)}
                color="secondary"
              >
                Edit (FR)
              </Button>
              {legalDocs.terms && (
                <>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() => handleViewVersions('terms')}
                  >
                    Versions
                  </Button>
                </>
              )}
            </CardActions>
          </Card>

          {/* Privacy Policy Card */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
                  Privacy Policy
                </Typography>
                <Chip
                  label={legalDocs.privacy?.status || 'Not Created'}
                  color={legalDocs.privacy?.status === 'published' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              {legalDocs.privacy ? (
                <>
                  <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '1.125rem' }}>
                    Version {legalDocs.privacy.version} • Last updated: {new Date(legalDocs.privacy.lastModified).toLocaleDateString()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.9375rem' }}>
                      Published: {legalDocs.privacy.publishedAt ? new Date(legalDocs.privacy.publishedAt).toLocaleDateString() : 'Not published'}
                    </Typography>
                    <Chip
                      label={legalDocs.privacy.contentFr ? 'FR Version: OK' : 'FR Version: Missing'}
                      size="small"
                      variant="outlined"
                      color={legalDocs.privacy.contentFr ? 'success' : 'warning'}
                    />
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '1.125rem' }}>
                  No Privacy Policy document created yet.
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => handleOpenLegalEditor('privacy', false)}
              >
                {legalDocs.privacy ? 'Edit (EN)' : 'Create'}
              </Button>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => handleOpenLegalEditor('privacy', true)}
                color="secondary"
              >
                Edit (FR)
              </Button>
              {legalDocs.privacy && (
                <>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() => handleViewVersions('privacy')}
                  >
                    Versions
                  </Button>
                </>
              )}
            </CardActions>
          </Card>
        </Box>
      </TabPanel>

      {/* Marketing Assets Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>Banners</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
            {assets.filter(a => a.category === 'banner').map((asset) => (
              <Card key={asset.id}>
                <Box
                  component="img"
                  src={asset.url}
                  alt={asset.name}
                  sx={{ width: '100%', height: 150, objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography variant="body2" noWrap>{asset.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatFileSize(asset.size)}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" href={asset.url} target="_blank">View</Button>
                  <IconButton size="small" color="error" onClick={() => handleDeleteAsset(asset.id)}>
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>Logos</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
            {assets.filter(a => a.category === 'logo').map((asset) => (
              <Card key={asset.id}>
                <Box
                  component="img"
                  src={asset.url}
                  alt={asset.name}
                  sx={{ width: '100%', height: 150, objectFit: 'contain', bgcolor: 'grey.100', p: 2 }}
                />
                <CardContent>
                  <Typography variant="body2" noWrap>{asset.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatFileSize(asset.size)}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" href={asset.url} target="_blank">View</Button>
                  <IconButton size="small" color="error" onClick={() => handleDeleteAsset(asset.id)}>
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>Promotional</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {assets.filter(a => a.category === 'promotional').map((asset) => (
              <Card key={asset.id}>
                <Box
                  component="img"
                  src={asset.url}
                  alt={asset.name}
                  sx={{ width: '100%', height: 150, objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography variant="body2" noWrap>{asset.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatFileSize(asset.size)}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" href={asset.url} target="_blank">View</Button>
                  <IconButton size="small" color="error" onClick={() => handleDeleteAsset(asset.id)}>
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>
        </Box>

        {assets.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography color="textSecondary" sx={{ fontSize: '1.125rem' }}>
              No assets yet. Click "Upload Asset" to add banners, logos, or promotional images.
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Page Editor Dialog */}
      <Dialog open={editorOpen} onClose={handleCloseEditor} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.875rem' }}>
          {currentPage ? 'Edit Page' : 'Create New Page'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Tabs
              value={editorLangTab}
              onChange={(e, v) => setEditorLangTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <Tab label="English (Default)" />
              <Tab label="French (Français)" />
            </Tabs>

            {editorLangTab === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Page Title (English)"
                  fullWidth
                  value={pageTitle}
                  onChange={(e) => {
                    setPageTitle(e.target.value);
                    if (!currentPage) {
                      setPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
                    }
                  }}
                  required
                  InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                />
                <TextField
                  label="URL Slug"
                  fullWidth
                  value={pageSlug}
                  onChange={(e) => setPageSlug(e.target.value)}
                  helperText="This will be the URL path for the page (e.g., 'privacy-policy')"
                  required
                  InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                />
                <TextField
                  label="Content (English)"
                  fullWidth
                  multiline
                  rows={12}
                  value={pageContent}
                  onChange={(e) => setPageContent(e.target.value)}
                  helperText="Use HTML or Markdown for formatting"
                  required
                  InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Titre de la page (Français)"
                  fullWidth
                  value={pageTitleFr}
                  onChange={(e) => setPageTitleFr(e.target.value)}
                  InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                />
                <TextField
                  label="Contenu (Français)"
                  fullWidth
                  multiline
                  rows={12}
                  value={pageContentFr}
                  onChange={(e) => setPageContentFr(e.target.value)}
                  helperText="Utilisez HTML ou Markdown pour le formatage"
                  InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                />
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '1.125rem' }}>SEO Metadata</Typography>
            <TextField
              label="SEO Title"
              fullWidth
              value={seoTitle}
              onChange={(e) => setSeoTitle(e.target.value)}
              helperText="Optimized title for search engines (50-60 characters)"
              InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
              FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
            />
            <TextField
              label="SEO Description"
              fullWidth
              multiline
              rows={2}
              value={seoDescription}
              onChange={(e) => setSeoDescription(e.target.value)}
              helperText="Meta description for search results (150-160 characters)"
              InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
              FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={pageStatus}
                label="Status"
                onChange={(e) => setPageStatus(e.target.value as 'draft' | 'published')}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditor}>Cancel</Button>
          <Button
            startIcon={<Preview />}
            onClick={() => {
              setCurrentPage({
                id: currentPage?.id || '',
                title: pageTitle,
                slug: pageSlug,
                content: pageContent,
                status: pageStatus,
                seoTitle: seoTitle,
                seoDescription: seoDescription,
                lastModified: new Date().toISOString(),
                version: currentPage?.version || 1
              });
              setPreviewOpen(true);
            }}
          >
            Preview
          </Button>
          <Button startIcon={<Save />} onClick={() => handleSavePage(false)}>
            Save as Draft
          </Button>
          <Button variant="contained" onClick={() => handleSavePage(true)}>
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.875rem' }}>
          Preview: {currentPage?.title}
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h4" gutterBottom sx={{ fontSize: '3rem' }}>{currentPage?.title}</Typography>
            <Divider sx={{ my: 2 }} />
            <Box
              dangerouslySetInnerHTML={{ __html: currentPage?.content || '' }}
              sx={{ '& p': { mb: 2 }, '& h1, & h2, & h3': { mt: 3, mb: 2 } }}
            />
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Asset Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.875rem' }}>Upload Marketing Asset</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: '1.125rem' }}>Asset Category</InputLabel>
              <Select
                value={assetCategory}
                label="Asset Category"
                onChange={(e) => setAssetCategory(e.target.value as any)}
                sx={{ fontSize: '1.125rem' }}
              >
                <MenuItem value="banner" sx={{ fontSize: '1.125rem' }}>Banner</MenuItem>
                <MenuItem value="logo" sx={{ fontSize: '1.125rem' }}>Logo</MenuItem>
                <MenuItem value="promotional" sx={{ fontSize: '1.125rem' }}>Promotional</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              fullWidth
            >
              Select File
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>

            {selectedFile && (
              <Alert severity="info">
                Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadAsset}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Legal Document Editor Dialog */}
      <Dialog open={legalEditorOpen} onClose={handleCloseLegalEditor} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontSize: '1.875rem' }}>
          Edit {currentLegalType === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Tabs
              value={editorLangTab}
              onChange={(e, v) => setEditorLangTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <Tab label="English (Default)" />
              <Tab label="French (Français)" />
            </Tabs>

            {editorLangTab === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Title (English)"
                  fullWidth
                  value={legalTitle}
                  onChange={(e) => setLegalTitle(e.target.value)}
                  required
                  InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontSize: '1.125rem', fontWeight: 500 }}>
                    Content (English)
                  </Typography>
                  <Box sx={{
                    '& .quill': {
                      height: '400px',
                      '& .ql-container': {
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        borderBottomLeftRadius: '4px',
                        borderBottomRightRadius: '4px',
                      },
                      '& .ql-editor': {
                        minHeight: '350px',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                      },
                      '& .ql-toolbar': {
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.23)',
                      },
                    }
                  }}>
                    {ReactQuillComponent ? (
                      <ReactQuillComponent
                        theme="snow"
                        value={legalContent}
                        onChange={setLegalContent}
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                            [{ 'size': ['small', false, 'large', 'huge'] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'align': [] }],
                            ['link', 'blockquote', 'code-block'],
                            ['clean']
                          ],
                          clipboard: {
                            matchVisual: false,
                          },
                        }}
                        formats={[
                          'header', 'size',
                          'bold', 'italic', 'underline', 'strike',
                          'color', 'background',
                          'list', 'bullet',
                          'align',
                          'link', 'blockquote', 'code-block'
                        ]}
                      />
                    ) : (
                      <TextField
                        label="Content (English)"
                        fullWidth
                        multiline
                        rows={20}
                        value={legalContent}
                        onChange={(e) => setLegalContent(e.target.value)}
                        helperText="Rich text editor not available."
                        required
                        InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                        FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Titre (Français)"
                  fullWidth
                  value={legalTitleFr}
                  onChange={(e) => setLegalTitleFr(e.target.value)}
                  InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontSize: '1.125rem', fontWeight: 500 }}>
                    Contenu (Français)
                  </Typography>
                  <Box sx={{
                    '& .quill': {
                      height: '400px',
                      '& .ql-container': {
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        borderBottomLeftRadius: '4px',
                        borderBottomRightRadius: '4px',
                      },
                      '& .ql-editor': {
                        minHeight: '350px',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                      },
                      '& .ql-toolbar': {
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.23)',
                      },
                    }
                  }}>
                    {ReactQuillComponent ? (
                      <ReactQuillComponent
                        theme="snow"
                        value={legalContentFr}
                        onChange={setLegalContentFr}
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                            [{ 'size': ['small', false, 'large', 'huge'] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'align': [] }],
                            ['link', 'blockquote', 'code-block'],
                            ['clean']
                          ],
                          clipboard: {
                            matchVisual: false,
                          },
                        }}
                        formats={[
                          'header', 'size',
                          'bold', 'italic', 'underline', 'strike',
                          'color', 'background',
                          'list', 'bullet',
                          'align',
                          'link', 'blockquote', 'code-block'
                        ]}
                      />
                    ) : (
                      <TextField
                        label="Contenu (Français)"
                        fullWidth
                        multiline
                        rows={20}
                        value={legalContentFr}
                        onChange={(e) => setLegalContentFr(e.target.value)}
                        helperText="Rich text editor not available."
                        InputLabelProps={{ sx: { fontSize: '1.125rem' } }}
                        FormHelperTextProps={{ sx: { fontSize: '0.9375rem' } }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, fontSize: '0.9375rem', display: 'block' }}>
                Use the toolbar above to format your content. This will be published immediately when you click 'Publish & Notify'.
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyUsers}
                  onChange={(e) => setNotifyUsers(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '1.125rem' }}>
                    Send email notification to all active users
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.9375rem' }}>
                    Users will be notified about the update via email
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLegalEditor}>Cancel</Button>
          <Button
            startIcon={<Save />}
            onClick={() => handleSaveLegalDocument(false)}
            variant="outlined"
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            startIcon={<Notifications />}
            onClick={() => handleSaveLegalDocument(true)}
            disabled={!legalContent.trim()}
          >
            Publish {notifyUsers ? '& Notify Users' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={versionsOpen} onClose={() => setVersionsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.875rem' }}>
          Version History - {currentLegalType === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {legalVersions.length === 0 ? (
              <Typography color="textSecondary" sx={{ fontSize: '1.125rem' }}>No versions found</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {legalVersions.map((version: any) => (
                  <Card key={version.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.3125rem' }}>
                          Version {version.version}
                        </Typography>
                        <Chip
                          label={version.status}
                          size="small"
                          color={version.status === 'published' ? 'success' : 'default'}
                        />
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.9375rem' }}>
                        Created: {new Date(version.createdAt).toLocaleString()}
                      </Typography>
                      {version.createdBy && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.9375rem' }}>
                          By: {version.createdBy}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <Alert onClose={() => setAlert({ ...alert, show: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContentPage;
